import { useCallback, useEffect, useMemo, useState } from 'react';
import { cn, useMediaQuery } from 'common/utils';
import { SearchIcon } from 'components/v2/atoms/icon';
import { UserTopicType } from 'service/user';

interface TopicSelectionDialogProps {
  isOpen: boolean;
  onSelect: (topic: UserTopicType) => void;
  onCreateNew?: (topicName: string) => void;
  topics: UserTopicType[];
  triggerRef?: React.RefObject<HTMLDivElement>;
}

const TopicSelectionDialog = ({
  isOpen,
  onSelect,
  onCreateNew,
  topics,
  triggerRef,
}: TopicSelectionDialogProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const isMobile = useMediaQuery('(max-width: 767px)');
  const filteredTopics = useMemo(() => {
    return topics.filter((topic) =>
      topic.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [topics, searchQuery]);

  // Calculate dropdown position with proper viewport handling
  const calculatePosition = useCallback(() => {
    if (!triggerRef?.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownHeight = 391; // Approximate height of dropdown
    const dropdownWidth = isMobile ? window.innerWidth - 32 : 400; // Match className widths
    const spacing = 8; // Spacing between trigger and dropdown
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    let top = rect.top - dropdownHeight - spacing;
    let left = rect.left;

    // Adjust for mobile
    if (isMobile) {
      top += 100;
      left = 16; // Center with 16px margins (calc(100vw-32px))
    } else {
      // Ensure dropdown doesn't go off the right edge
      if (left + dropdownWidth > viewportWidth) {
        left = viewportWidth - dropdownWidth - 16; // 16px margin from edge
      }

      // Ensure dropdown doesn't go off the left edge
      if (left < 16) {
        left = 16;
      }
    }

    // If not enough space above, position below the trigger
    if (top < 16) {
      // 16px margin from top
      top = rect.bottom + spacing;

      // If positioning below would also go off screen, position in viewport center
      if (top + dropdownHeight > viewportHeight - 16) {
        top = Math.max(16, (viewportHeight - dropdownHeight) / 2);
      }
    }

    setPosition({ top, left });
  }, [isMobile, triggerRef]);

  useEffect(() => {
    calculatePosition();
  }, [isOpen, calculatePosition]);

  // Recalculate position on window resize
  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      calculatePosition();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, calculatePosition]);

  const handleTopicSelect = (topic: UserTopicType) => {
    onSelect(topic);
    setSearchQuery('');
  };

  const handleCreateNewTopic = () => {
    if (onCreateNew && searchQuery.trim()) {
      onCreateNew(searchQuery.trim());
      setSearchQuery('');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="all-font-sans fixed z-50 w-[calc(100vw-32px)] max-w-[calc(100vw-32px)] rounded-xl border border-gray-200 bg-white shadow-lg sm:w-[400px] sm:max-w-[400px]"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      {/* Header */}
      <div className="rounded-t-xl border-b border-pp-border-primary bg-[#F8F9FA] p-3">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold text-pp-text-primary">
            トピックを選択
          </h3>
          <div className="text-xs text-pp-text-secondary">※1つだけ選択可能</div>
        </div>
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-pp-text-secondary" />
          <input
            type="text"
            placeholder="トピックを検索..."
            value={searchQuery}
            maxLength={12}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="border-gray-20 w-full rounded-lg border bg-white py-2 pl-9 pr-4 text-sm focus:border-pp-text-tertiary focus:outline-none"
          />
        </div>
      </div>

      {/* Search Input */}

      {/* Topics List */}
      <div className="h-72">
        <div
          className={cn('scrollbar-default h-72 overflow-y-auto p-2', {
            'h-56': searchQuery.trim(),
          })}
        >
          {filteredTopics.map((topic, index) => (
            <button
              key={`${topic.name}-${index}`}
              onClick={() => handleTopicSelect(topic)}
              className="flex w-full items-center justify-between rounded-lg px-4 py-2.5 text-left hover:bg-[#F8F9FA]"
            >
              <span className="text-sm font-medium text-pp-text-primary">
                {topic.name}
              </span>
              <div className="flex space-x-2">
                {topic.display_type === 'hot_topic' && (
                  <span className="rounded-full bg-pp-border-primary px-2 py-0.5 text-[11px] font-semibold text-pp-text-secondary">
                    メイン
                  </span>
                )}
                {topic.display_type === 'recommended_topic' && (
                  <span className="rounded-full bg-[#4ECDC4] px-2 py-0.5 text-[11px] font-semibold text-white">
                    推奨
                  </span>
                )}
              </div>
            </button>
          ))}
          {filteredTopics.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-pp-text-secondary">
              {searchQuery.trim()
                ? `「${searchQuery}」に一致するトピックが見つかりません`
                : '検索結果が見つかりません'}
            </div>
          )}
        </div>
        {/* Add New Topic Button */}
        {searchQuery.trim() && (
          <div className="h-16 border-t border-pp-border-primary bg-[#F0F8FF] p-2">
            <button
              onClick={handleCreateNewTopic}
              className="border-1 flex w-full items-center justify-center rounded-lg border-dashed border-pp-text-tertiary bg-white px-2 py-[10px] text-center hover:border-pp-text-primary hover:bg-[#F8F9FA]"
            >
              <span className="text-sm text-pp-text-primary">
                +「{searchQuery}」として作成
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopicSelectionDialog;
